import React, { useEffect, useRef } from "react";
import { Provider } from "react-redux";
import { I18nextProvider } from "react-i18next";
import { camelizeKeys } from "humps";
import { useRouter } from "next/router";
import { parse } from "qs";
import { store } from "main/javascripts/store";
import { HeaderContainer } from "main/javascripts/containers/organisms/Header/HeaderContainer";
import {
  defaultStyles,
  extended,
  GLOBALS,
} from "main/javascripts/styles/globalStyle";
import i18n from "i18n";
import { CruiseHead } from "main/javascripts/components/cruise/CruiseHead";
import { useConstructor } from "main/javascripts/hooks/common/useConstructor";
import { useUserAccount } from "main/javascripts/hooks/common/useUserAccount";
import { CruiseShowMain } from "main/javascripts/features/cruise/components/CruiseShowMain";
import { Footer } from "main/javascripts/components/footer/Footer";
import {
  fetchCruise,
  initCruise,
  setBotDetected,
} from "main/javascripts/features/cruise/cruiseSlice";
import { Cruise } from "main/javascripts/types/cruise";
import { HOST } from "main/javascripts/constants/Constants";
import { initCruiseShowParam } from "main/javascripts/slices/paramSlice";
// import { loadVConsole } from "main/javascripts/utils/DebugUtil";

interface IProps {
  // currentUser: ICurrentUser | null;
  cruise: Cruise.Cruise;
  locale: string;
}

function CruiseShow(props: IProps) {
  const { cruise, locale } = props;
  const origin = HOST;
  const router = useRouter();
  const params = parse(router.asPath.split("?")[1]);
  const noindex = cruise.noindex || false;
  // getStaticPropsから渡される値がブラウザバックで変化しないため
  // routerからcruiseIdを取得している
  const { cruiseId } = router.query;

  const prevCruiseId = useRef(cruiseId);

  useUserAccount(store, true);

  const initializeData = () => {
    const camelizedKeysParams = camelizeKeys(params);
    store.dispatch(initCruise(cruise)); // RTK
    store.dispatch(initCruiseShowParam(camelizedKeysParams)); // RTK
  };

  useConstructor(() => {
    initializeData();
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
    extended.css(defaultStyles[GLOBALS]);
  });

  useEffect(() => {
    // ボット検知ロジック
    const userAgent = navigator.userAgent.toLowerCase();
    const botKeywords = [
      "bot",
      "spider",
      "crawler",
      "googlebot",
      "bingbot",
      "yahoo! slurp",
      "duckduckbot",
      "baiduspider",
      "yandexbot",
      "sogou",
      "exabot",
      "facebot",
      "facebookexternalhit",
      "twitterbot",
      "linkedinbot",
      "embedly",
      "quora link preview",
      "showyoubot",
      "outbrain",
      "pinterest",
      "slackbot",
      "vkshare",
      "w3c_validator",
      "redditbot",
      "applebot",
      "whatsapp",
      "telegrambot",
      "discordbot",
    ];
    const botDetected = botKeywords.some((keyword) =>
      userAgent.includes(keyword)
    );
    store.dispatch(setBotDetected(botDetected));
  }, []);

  useEffect(() => {
    // 初期化後にルートが変更された場合再設定する
    // cruiseIdとprevCruiseId.currentは最初undefinedが入るので
    // 値を持っている状態の時だけ比較する
    if (cruiseId && prevCruiseId.current && prevCruiseId.current !== cruiseId) {
      initializeData();
      // URLクエリを更新している影響か、
      // ブラウザバックでcruiseが更新されないので再取得する
      // ただしLink遷移時などcruiseが更新されている場合はfetchしない
      if (cruise.uniqueId !== cruiseId) {
        store.dispatch(
          fetchCruise({
            uniqueId: String(cruiseId),
            params: parse(params),
          })
        );
      }
    }
    prevCruiseId.current = cruiseId;
  }, [cruiseId]);

  return (
    <Provider store={store}>
      <CruiseHead
        origin={origin}
        title={cruise.metatitle}
        description={cruise.metadescription}
        noindex={noindex}
      />
      <I18nextProvider i18n={i18n}>
        <>
          <HeaderContainer />
          <CruiseShowMain />
          <Footer />
        </>
      </I18nextProvider>
    </Provider>
  );
}

export async function getStaticProps(ctx) {
  const locale = ctx.locale;
  const param = ctx.params;
  const cruiseId = param.cruiseId;
  const data = await store
    .dispatch(
      fetchCruise({
        uniqueId: cruiseId,
        params: parse(param),
        isServerSide: true,
      })
    )
    .unwrap();

  if (data.cruise && data.cruise.noindex && data.cruise.redirectCruiseId) {
    return {
      redirect: {
        destination: `/cruises/${data.cruise.redirectCruiseId}`,
        permanent: true,
      },
    };
  }

  return {
    props: {
      locale,
      ...data,
    },
    revalidate: 60,
  };
}

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export default CruiseShow;
